import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Calendar as CalendarIcon, Plus, Trash2, Pencil, Copy, Search, Filter,
  Linkedin, Facebook, Instagram, Youtube, Music2, Hash, Image as ImageIcon,
  Upload, X, Link as LinkIcon, Film, Loader2, ChevronLeft, ChevronRight,
  LayoutGrid, CalendarDays, List, Play,
} from 'lucide-react';
import { socialAPI, socialMediaAPI } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useT } from '@/lib/LanguageContext';
import { toast } from 'sonner';
import {
  format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameDay, isToday, addMonths, subMonths,
  addWeeks, subWeeks, addDays, subDays, isSameMonth,
} from 'date-fns';

// ─── Constants ────────────────────────────────────────────────────────────────

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: Instagram, color: 'bg-pink-100 text-pink-700' },
  { value: 'facebook',  label: 'Facebook',  icon: Facebook,  color: 'bg-blue-100 text-blue-700' },
  { value: 'linkedin',  label: 'LinkedIn',  icon: Linkedin,  color: 'bg-sky-100 text-sky-800' },
  { value: 'tiktok',    label: 'TikTok',    icon: Music2,    color: 'bg-slate-900 text-white' },
  { value: 'twitter',   label: 'X / Twitter', icon: Hash,    color: 'bg-slate-100 text-slate-800' },
  { value: 'youtube',   label: 'YouTube',   icon: Youtube,   color: 'bg-red-100 text-red-700' },
];

const CONTENT_TYPES = [
  { value: 'post',     label: 'Post' },
  { value: 'story',    label: 'Story' },
  { value: 'reel',     label: 'Reel' },
  { value: 'video',    label: 'Video' },
  { value: 'short',    label: 'Short' },
  { value: 'carousel', label: 'Carousel' },
];

const STATUSES = [
  { value: 'draft',     label: 'Draft',     color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'ready',     label: 'Ready',     color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'published', label: 'Published', color: 'bg-teal-100 text-teal-700 border-teal-300' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'archived',  label: 'Archived',  color: 'bg-slate-100 text-slate-500 border-slate-300' },
];

// Status dot colours for calendar pills
const STATUS_DOT = {
  draft:     'bg-slate-400',
  scheduled: 'bg-blue-500',
  ready:     'bg-emerald-500',
  published: 'bg-teal-500',
  cancelled: 'bg-red-400',
  archived:  'bg-slate-300',
};

const ALL = '__all__';
const NONE = '__none__';

const platformMeta  = (v) => PLATFORMS.find((p) => p.value === v) || { label: v, icon: Hash, color: 'bg-slate-100 text-slate-700' };
const statusMeta    = (v) => STATUSES.find((s) => s.value === v)  || { label: v, color: 'bg-slate-100 text-slate-700 border-slate-300' };
const contentTypeLabel = (v) => CONTENT_TYPES.find((c) => c.value === v)?.label || v;

const toIsoOrNull = (lv) => { if (!lv) return null; const d = new Date(lv); return isNaN(d) ? null : d.toISOString(); };
const toLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? parseISO(iso) : new Date(iso);
    if (isNaN(d)) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch { return ''; }
};
const parseCsvList = (s) => (s||'').split(/[,\n]/).map(v=>v.trim()).filter(Boolean);

const emptyForm = {
  platform: 'instagram', content_type: 'post', title: '', content: '',
  hashtags: '', notes: '', scheduled_for: '', status: 'draft', campaign_tag: '', project_id: '',
};

const isVideoUrl = (url) => /\.(mp4|mov|webm|avi|m4v)$/i.test(url);

// ─── Post Preview Dialog ───────────────────────────────────────────────────────

const PostPreviewDialog = ({ post, onClose, onEdit, onDelete, projectName }) => {
  const [mediaIdx, setMediaIdx] = useState(0);
  if (!post) return null;
  const plat = platformMeta(post.platform);
  const stat = statusMeta(post.status);
  const Icon = plat.icon;
  const when = post.scheduled_for ? parseISO(post.scheduled_for) : null;
  const media = post.media_urls || [];
  const currentMedia = media[mediaIdx];
  const isVideo = currentMedia && isVideoUrl(currentMedia);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto p-0">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${plat.color}`}>
              <Icon className="w-4 h-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#1E2A33]">{post.title || plat.label}</p>
              <p className="text-xs text-[#5C6670]">
                {plat.label} · {contentTypeLabel(post.content_type || 'post')}
                {projectName ? ` · ${projectName}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${stat.color}`}>
              {stat.label}
            </Badge>
          </div>
        </div>

        {/* Media */}
        {media.length > 0 && (
          <div className="relative bg-black">
            {isVideo ? (
              <video
                key={currentMedia}
                src={currentMedia}
                controls
                className="w-full max-h-[360px] object-contain"
              />
            ) : (
              <img
                src={currentMedia}
                alt="Post media"
                className="w-full max-h-[360px] object-contain"
              />
            )}
            {media.length > 1 && (
              <>
                <button
                  onClick={() => setMediaIdx((i) => (i - 1 + media.length) % media.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setMediaIdx((i) => (i + 1) % media.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {media.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setMediaIdx(i)}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${i === mediaIdx ? 'bg-white' : 'bg-white/40'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Body */}
        <div className="p-4 space-y-3">
          {when && (
            <div className="flex items-center gap-1.5 text-xs text-[#5C6670]">
              <CalendarIcon className="w-3.5 h-3.5" />
              {format(when, 'EEEE, MMMM d, yyyy · h:mm a')}
            </div>
          )}

          {post.content && (
            <p className="text-sm text-[#1E2A33] whitespace-pre-wrap leading-relaxed">{post.content}</p>
          )}

          {(post.hashtags||[]).length > 0 && (
            <p className="text-sm text-sky-600">
              {post.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ')}
            </p>
          )}

          {post.campaign_tag && (
            <div>
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                {post.campaign_tag}
              </span>
            </div>
          )}

          {post.notes && (
            <div className="bg-[#FBF8F2] rounded-lg p-3 border border-[#E3DDD1]">
              <p className="text-xs text-[#5C6670] font-medium mb-1">Internal notes</p>
              <p className="text-xs text-[#1E2A33] whitespace-pre-wrap">{post.notes}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between p-4 border-t bg-[#FBF8F2]">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { onDelete(post); onClose(); }}
              className="text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
            <Button size="sm" onClick={() => { onEdit(post); onClose(); }}
              className="bg-[#A62B1F] hover:bg-[#8C2318] text-white">
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Board view ────────────────────────────────────────────────────────────────

const BoardPost = ({ post, projectName, onPreview, onEdit, onDuplicate, onDelete }) => {
  const plat = platformMeta(post.platform);
  const stat = statusMeta(post.status);
  const Icon = plat.icon;
  const when = post.scheduled_for ? parseISO(post.scheduled_for) : null;
  const media = post.media_urls || [];
  const firstMedia = media[0];
  const firstIsVideo = firstMedia && isVideoUrl(firstMedia);

  return (
    <div
      className="border border-[#E3DDD1] rounded-lg bg-white shadow-sm hover:shadow transition cursor-pointer overflow-hidden"
      onClick={() => onPreview(post)}
      data-testid={`social-card-${post._id}`}
    >
      {/* Media thumbnail */}
      {firstMedia && (
        <div className="relative w-full aspect-video bg-black overflow-hidden">
          {firstIsVideo ? (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
              <Play className="w-8 h-8 text-white/70" />
              <Film className="absolute bottom-2 right-2 w-4 h-4 text-white/50" />
            </div>
          ) : (
            <img src={firstMedia} alt="" className="w-full h-full object-cover" />
          )}
          {media.length > 1 && (
            <span className="absolute top-1.5 right-1.5 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
              +{media.length - 1}
            </span>
          )}
        </div>
      )}

      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`inline-flex items-center justify-center w-7 h-7 rounded flex-shrink-0 ${plat.color}`}>
              <Icon className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#1E2A33] truncate" style={{ fontFamily: 'Manrope' }}>
                {post.title || post.content?.slice(0, 60) || '(untitled)'}
              </p>
              <p className="text-[11px] text-[#5C6670] truncate">
                {plat.label} · {contentTypeLabel(post.content_type || 'post')}
                {projectName ? ` · ${projectName}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onDuplicate(post)} title="Duplicate">
              <Copy className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onEdit(post)} title="Edit">
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-red-600 hover:text-red-700" onClick={() => onDelete(post)} title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {post.content && (
          <p className="text-xs text-[#5C6670] mt-2 line-clamp-3 whitespace-pre-wrap">{post.content}</p>
        )}

        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${stat.color}`}>
            {stat.label}
          </Badge>
          {when && (
            <span className="inline-flex items-center gap-1 text-[11px] text-[#5C6670]">
              <CalendarIcon className="w-3 h-3" />
              {format(when, 'MMM d · HH:mm')}
            </span>
          )}
          {post.campaign_tag && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
              {post.campaign_tag}
            </span>
          )}
        </div>
        {(post.hashtags||[]).length > 0 && (
          <p className="text-[11px] text-sky-700 mt-1.5 line-clamp-1">
            {post.hashtags.map(h => h.startsWith('#') ? h : `#${h}`).join(' ')}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Calendar pill (shared by month + week) ────────────────────────────────────

const CalPill = ({ post, onClick }) => {
  const plat = platformMeta(post.platform);
  const Icon = plat.icon;
  const dot  = STATUS_DOT[post.status] || 'bg-slate-400';
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(post); }}
      className="w-full text-left flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-[#F3EFE7] transition-colors group"
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
      <Icon className="w-3 h-3 flex-shrink-0 text-[#5C6670]" />
      <span className="text-[11px] text-[#1E2A33] truncate leading-tight">
        {post.title || post.content?.slice(0, 30) || '(untitled)'}
      </span>
    </button>
  );
};

// ─── Month View ────────────────────────────────────────────────────────────────

const MonthView = ({ posts, currentDate, onPreview }) => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd   = endOfMonth(currentDate);
  const gridStart  = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd    = endOfWeek(monthEnd,   { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const postsByDay = useMemo(() => {
    const map = {};
    posts.forEach(p => {
      if (!p.scheduled_for) return;
      const key = format(parseISO(p.scheduled_for), 'yyyy-MM-dd');
      (map[key] = map[key] || []).push(p);
    });
    return map;
  }, [posts]);

  const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  return (
    <div className="border border-[#E3DDD1] rounded-lg overflow-hidden">
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 bg-[#FBF8F2] border-b border-[#E3DDD1]">
        {DOW.map(d => (
          <div key={d} className="py-2 text-center text-xs font-semibold text-[#5C6670]">{d}</div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 divide-x divide-y divide-[#E3DDD1]">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const dayPosts = postsByDay[key] || [];
          const inMonth  = isSameMonth(day, currentDate);
          const today    = isToday(day);
          return (
            <div
              key={key}
              className={`min-h-[100px] p-1.5 ${inMonth ? 'bg-white' : 'bg-[#FBF8F2]'}`}
            >
              <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full
                ${today ? 'bg-[#A62B1F] text-white' : inMonth ? 'text-[#1E2A33]' : 'text-[#D3CCBE]'}`}>
                {format(day, 'd')}
              </div>
              <div className="space-y-0.5">
                {dayPosts.slice(0, 3).map(p => (
                  <CalPill key={p._id} post={p} onClick={onPreview} />
                ))}
                {dayPosts.length > 3 && (
                  <p className="text-[10px] text-[#5C6670] pl-1">+{dayPosts.length - 3} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Week View ─────────────────────────────────────────────────────────────────

const WeekView = ({ posts, currentDate, onPreview }) => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const postsByDay = useMemo(() => {
    const map = {};
    posts.forEach(p => {
      if (!p.scheduled_for) return;
      const key = format(parseISO(p.scheduled_for), 'yyyy-MM-dd');
      (map[key] = map[key] || []).push(p);
    });
    return map;
  }, [posts]);

  return (
    <div className="border border-[#E3DDD1] rounded-lg overflow-hidden">
      <div className="grid grid-cols-7 divide-x divide-[#E3DDD1]">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const dayPosts = postsByDay[key] || [];
          const today = isToday(day);
          return (
            <div key={key} className="min-h-[300px]">
              {/* Column header */}
              <div className={`p-2 text-center border-b border-[#E3DDD1] ${today ? 'bg-[#FFF5F4]' : 'bg-[#FBF8F2]'}`}>
                <p className="text-[11px] text-[#5C6670] font-medium">{format(day, 'EEE')}</p>
                <div className={`text-sm font-bold mx-auto w-7 h-7 flex items-center justify-center rounded-full mt-0.5
                  ${today ? 'bg-[#A62B1F] text-white' : 'text-[#1E2A33]'}`}>
                  {format(day, 'd')}
                </div>
              </div>
              {/* Posts */}
              <div className="p-1.5 space-y-1.5">
                {dayPosts.map((post) => {
                  const plat = platformMeta(post.platform);
                  const Icon = plat.icon;
                  const stat = statusMeta(post.status);
                  const media = post.media_urls || [];
                  return (
                    <button
                      key={post._id}
                      onClick={() => onPreview(post)}
                      className="w-full text-left border border-[#E3DDD1] rounded-lg bg-white hover:shadow-sm transition overflow-hidden"
                    >
                      {media[0] && !isVideoUrl(media[0]) && (
                        <img src={media[0]} alt="" className="w-full h-16 object-cover" />
                      )}
                      {media[0] && isVideoUrl(media[0]) && (
                        <div className="w-full h-16 bg-slate-900 flex items-center justify-center">
                          <Play className="w-5 h-5 text-white/60" />
                        </div>
                      )}
                      <div className="p-1.5">
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className={`inline-flex items-center justify-center w-4 h-4 rounded ${plat.color}`}>
                            <Icon className="w-2.5 h-2.5" />
                          </span>
                          <Badge variant="outline" className={`text-[9px] uppercase tracking-wide py-0 px-1 ${stat.color}`}>
                            {stat.label}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-[#1E2A33] font-medium line-clamp-2 leading-tight">
                          {post.title || post.content?.slice(0, 60) || '(untitled)'}
                        </p>
                        {post.scheduled_for && (
                          <p className="text-[10px] text-[#5C6670] mt-0.5">
                            {format(parseISO(post.scheduled_for), 'h:mm a')}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
                {dayPosts.length === 0 && (
                  <p className="text-[11px] text-[#D3CCBE] text-center py-4">—</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Day View ──────────────────────────────────────────────────────────────────

const DayView = ({ posts, currentDate, onPreview, onEdit, onDuplicate, onDelete, showingAll, projectName }) => {
  const dayPosts = useMemo(() =>
    posts
      .filter(p => p.scheduled_for && isSameDay(parseISO(p.scheduled_for), currentDate))
      .sort((a,b) => new Date(a.scheduled_for) - new Date(b.scheduled_for)),
    [posts, currentDate]
  );
  const unscheduled = useMemo(() => posts.filter(p => !p.scheduled_for), [posts]);

  if (dayPosts.length === 0 && unscheduled.length === 0) {
    return (
      <div className="border border-[#E3DDD1] rounded-lg py-16 text-center bg-white">
        <CalendarIcon className="w-8 h-8 mx-auto text-[#D3CCBE]" />
        <p className="mt-2 text-sm text-[#5C6670]">No posts scheduled for this day</p>
      </div>
    );
  }

  const PostRow = ({ post }) => {
    const plat = platformMeta(post.platform);
    const stat = statusMeta(post.status);
    const Icon = plat.icon;
    const media = post.media_urls || [];
    return (
      <div
        className="flex gap-4 p-4 border border-[#E3DDD1] rounded-lg bg-white hover:shadow-sm transition cursor-pointer"
        onClick={() => onPreview(post)}
      >
        {/* Time */}
        <div className="w-14 flex-shrink-0 text-center">
          {post.scheduled_for ? (
            <>
              <p className="text-sm font-bold text-[#1E2A33]">{format(parseISO(post.scheduled_for), 'h:mm')}</p>
              <p className="text-[10px] text-[#5C6670]">{format(parseISO(post.scheduled_for), 'a')}</p>
            </>
          ) : (
            <p className="text-xs text-[#9AA3AB]">—</p>
          )}
        </div>
        {/* Thumbnail */}
        {media[0] && (
          <div className="w-20 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-black">
            {isVideoUrl(media[0]) ? (
              <div className="w-full h-full flex items-center justify-center bg-slate-800">
                <Play className="w-5 h-5 text-white/60" />
              </div>
            ) : (
              <img src={media[0]} alt="" className="w-full h-full object-cover" />
            )}
          </div>
        )}
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${plat.color}`}>
              <Icon className="w-3.5 h-3.5" />
            </span>
            <p className="text-sm font-semibold text-[#1E2A33] truncate">
              {post.title || post.content?.slice(0, 60) || '(untitled)'}
            </p>
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ml-auto flex-shrink-0 ${stat.color}`}>
              {stat.label}
            </Badge>
          </div>
          {post.content && <p className="text-xs text-[#5C6670] line-clamp-2">{post.content}</p>}
          <div className="flex gap-1 mt-1.5">
            {post.campaign_tag && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{post.campaign_tag}</span>
            )}
            {media.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                {media.length} media
              </span>
            )}
          </div>
        </div>
        {/* Actions */}
        <div className="flex items-start gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDuplicate(post)}><Copy className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(post)}><Pencil className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => onDelete(post)}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {dayPosts.map(p => <PostRow key={p._id} post={p} />)}
      {unscheduled.length > 0 && (
        <>
          <p className="text-xs font-semibold text-[#5C6670] uppercase tracking-widest mt-4 mb-2">Unscheduled</p>
          {unscheduled.map(p => <PostRow key={p._id} post={p} />)}
        </>
      )}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

export default function SocialCalendar() {
  const { projects } = useProject();
  const t = useT();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [projectFilter, setProjectFilter] = useState(ALL);
  const [platformFilter, setPlatformFilter] = useState(ALL);
  const [contentTypeFilter, setContentTypeFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [search, setSearch] = useState('');

  // View state
  const [viewMode, setViewMode] = useState('month'); // 'board' | 'month' | 'week' | 'day'
  const [currentDate, setCurrentDate] = useState(new Date());

  // Preview
  const [previewPost, setPreviewPost] = useState(null);

  const location = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => {
    if (location.state?.openNew) { setDialogOpen(true); window.history.replaceState({}, ''); }
  }, [location.state]);

  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const projectName = (id) => projects.find((p) => p._id === id)?.name;

  const loadPosts = async () => {
    setLoading(true); setErrorMsg('');
    try {
      const params = (projectFilter === ALL || projectFilter === NONE) ? { limit: 500 } : { project_id: projectFilter, limit: 500 };
      const res = await socialAPI.list(params);
      const list = Array.isArray(res.data) ? res.data : [];
      setPosts(projectFilter === NONE ? list.filter(p => !p.project_id) : list);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || 'Failed to load posts');
      setPosts([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPosts(); }, [projectFilter]); // eslint-disable-line

  // ── Media upload state ──────────────────────────────────────────────────────
  const [mediaItems, setMediaItems] = useState([]);
  const fileInputRef = useRef(null);
  const [urlInput, setUrlInput] = useState('');

  const handleFilesSelected = useCallback(async (files) => {
    const newItems = Array.from(files).map((file) => ({
      id: Math.random().toString(36).slice(2),
      url: '', filename: file.name, contentType: file.type,
      isImage: file.type.startsWith('image/'),
      preview: URL.createObjectURL(file), uploading: true, file,
    }));
    setMediaItems((prev) => [...prev, ...newItems]);

    for (const item of newItems) {
      try {
        const res = await socialMediaAPI.upload(item.file);
        const { id } = res.data;
        const serverUrl = socialMediaAPI.serveUrl(id);
        URL.revokeObjectURL(item.preview);                       // revoke old blob URL
        setMediaItems((prev) =>
          prev.map((m) =>
            m.id === item.id
              ? { ...m, id: `uploaded:${id}`, url: serverUrl, preview: serverUrl, uploading: false }
              : m
          )
        );
      } catch {
        toast.error(`Failed to upload ${item.filename}`);
        URL.revokeObjectURL(item.preview);
        setMediaItems((prev) => prev.filter((m) => m.id !== item.id));
      }
    }
  }, []);

  const addUrlMedia = () => {
    const url = urlInput.trim();
    if (!url) return;
    const isImage = !isVideoUrl(url);
    setMediaItems((prev) => [...prev, { id: `url:${url}`, url, filename: url.split('/').pop() || url, contentType: isImage ? 'image' : 'video', isImage, preview: url, uploading: false }]);
    setUrlInput('');
  };
  const removeMediaItem = (id) => setMediaItems((prev) => prev.filter((m) => m.id !== id));

  const openCreate = () => {
    setEditingPost(null);
    const inheritedProjectId = (projectFilter !== ALL && projectFilter !== NONE) ? projectFilter : '';
    setForm({ ...emptyForm, project_id: inheritedProjectId });
    setMediaItems([]); setUrlInput(''); setDialogOpen(true);
  };
  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      platform: post.platform || 'instagram', content_type: post.content_type || 'post',
      title: post.title || '', content: post.content || '',
      hashtags: (post.hashtags || []).join(', '), notes: post.notes || '',
      scheduled_for: toLocalInput(post.scheduled_for), status: post.status || 'draft',
      campaign_tag: post.campaign_tag || '', project_id: post.project_id || '',
    });
    setMediaItems(
      (post.media_urls || []).map((url) => ({
        id: `existing:${url}`, url, filename: url.split('/').pop() || url,
        contentType: isVideoUrl(url) ? 'video/mp4' : 'image/jpeg',
        isImage: !isVideoUrl(url), preview: url, uploading: false,
      }))
    );
    setUrlInput(''); setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.content.trim() && !form.title.trim() && !form.notes.trim()) {
      toast.error('Add a title, caption, or notes before saving'); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        project_id: form.project_id || null, platform: form.platform, content_type: form.content_type,
        title: form.title.trim() || null, content: form.content,
        hashtags: parseCsvList(form.hashtags), notes: form.notes || null,
        media_urls: mediaItems.filter((m) => !m.uploading && m.url).map((m) => m.url),
        scheduled_for: toIsoOrNull(form.scheduled_for), status: form.status,
        campaign_tag: form.campaign_tag || null,
      };
      if (editingPost) { await socialAPI.update(editingPost._id, payload); toast.success('Post updated'); }
      else { await socialAPI.create(payload); toast.success('Post scheduled'); }
      setDialogOpen(false); setEditingPost(null); setForm({ ...emptyForm });
      setMediaItems([]); setUrlInput(''); await loadPosts();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to save post';
      toast.error(typeof detail === 'string' ? detail : 'Failed to save post');
    } finally { setSubmitting(false); }
  };

  const duplicate = async (post) => {
    try { await socialAPI.duplicate(post._id); toast.success('Post duplicated'); await loadPosts(); }
    catch { toast.error('Failed to duplicate post'); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await socialAPI.delete(deleteTarget._id);
      setPosts((prev) => prev.filter((p) => p._id !== deleteTarget._id));
      toast.success('Post deleted');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete post'); }
    finally { setDeleteTarget(null); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return posts.filter((p) => {
      if (platformFilter !== ALL && p.platform !== platformFilter) return false;
      if (contentTypeFilter !== ALL && (p.content_type||'post') !== contentTypeFilter) return false;
      if (statusFilter !== ALL && p.status !== statusFilter) return false;
      if (q) {
        const hay = `${p.title||''} ${p.content||''} ${(p.hashtags||[]).join(' ')} ${p.campaign_tag||''} ${p.notes||''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [posts, platformFilter, contentTypeFilter, statusFilter, search]);

  // Kanban columns (board view)
  const columns = useMemo(() => {
    const buckets = { draft:[], scheduled:[], ready:[], published:[], cancelled:[], archived:[] };
    const sorted = [...filtered].sort((a,b) => {
      const av = a.scheduled_for ? new Date(a.scheduled_for).getTime() : Infinity;
      const bv = b.scheduled_for ? new Date(b.scheduled_for).getTime() : Infinity;
      return av - bv;
    });
    sorted.forEach((p) => { const k = buckets[p.status] ? p.status : 'draft'; buckets[k].push(p); });
    return STATUSES.filter(s => s.value !== 'archived' || buckets.archived.length > 0)
      .map(s => ({ ...s, posts: buckets[s.value] || [] }));
  }, [filtered]);

  // Nav helpers
  const navigate = (dir) => {
    if (viewMode === 'month') setCurrentDate(d => dir > 0 ? addMonths(d, 1) : subMonths(d, 1));
    else if (viewMode === 'week') setCurrentDate(d => dir > 0 ? addWeeks(d, 1) : subWeeks(d, 1));
    else if (viewMode === 'day')  setCurrentDate(d => dir > 0 ? addDays(d, 1)  : subDays(d, 1));
  };
  const navLabel = () => {
    if (viewMode === 'month') return format(currentDate, 'MMMM yyyy');
    if (viewMode === 'week') {
      const ws = startOfWeek(currentDate, { weekStartsOn: 0 });
      const we = endOfWeek(currentDate, { weekStartsOn: 0 });
      return `${format(ws, 'MMM d')} – ${format(we, 'MMM d, yyyy')}`;
    }
    if (viewMode === 'day') return format(currentDate, 'EEEE, MMMM d, yyyy');
    return '';
  };

  const showingAll = projectFilter === ALL;
  const totalCount = posts.length;
  const filteredCount = filtered.length;

  return (
    <Layout title={t('social.title')}>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-[#1E2A33]">
              <CalendarIcon className="w-5 h-5" />
              {t('social.title')}
            </CardTitle>
            <p className="text-sm text-[#5C6670] mt-1">
              {showingAll ? 'All projects' : projectName(projectFilter) || 'Selected project'} — {totalCount} post{totalCount===1?'':'s'}
              {filteredCount !== totalCount && ` (${filteredCount} shown)`}
            </p>
            <p className="text-xs text-[#9AA3AB] mt-0.5">Planning only — posting is handled externally from the saved schedule.</p>
          </div>
          <Button onClick={openCreate} disabled={!projects.length} className="bg-[#A62B1F] hover:bg-[#8C2318] text-white">
            <Plus className="w-4 h-4 mr-2" /> {t('social.new_post')}
          </Button>
        </CardHeader>

        <CardContent>
          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6670]" />
              <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search posts, captions, hashtags..." className="pl-9" />
            </div>
            <Filter className="w-4 h-4 text-[#5C6670]" />
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Project" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All projects</SelectItem>
                <SelectItem value={NONE}>No project</SelectItem>
                {projects.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Platform" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All platforms</SelectItem>
                {PLATFORMS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={contentTypeFilter} onValueChange={setContentTypeFilter}>
              <SelectTrigger className="w-[130px]"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {CONTENT_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* View toggle + nav */}
          <div className="flex items-center justify-between mb-4">
            {/* View mode buttons */}
            <div className="flex items-center gap-1 bg-[#F3EFE7] rounded-lg p-1">
              {[
                { id: 'month', Icon: CalendarDays, label: 'Month' },
                { id: 'week',  Icon: CalendarIcon, label: 'Week'  },
                { id: 'day',   Icon: List,         label: 'Day'   },
                { id: 'board', Icon: LayoutGrid,   label: 'Board' },
              ].map(({ id, Icon: VI, label }) => (
                <button
                  key={id}
                  onClick={() => setViewMode(id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all
                    ${viewMode === id ? 'bg-white shadow text-[#1E2A33]' : 'text-[#5C6670] hover:text-[#1E2A33]'}`}
                >
                  <VI className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>

            {/* Date navigation (hidden for board) */}
            {viewMode !== 'board' && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Today</Button>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium text-[#1E2A33] min-w-[180px] text-center">{navLabel()}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="py-12 text-center text-[#5C6670]">{t('social.loading')}</div>
          ) : errorMsg ? (
            <div className="py-12 text-center">
              <p className="text-red-600 text-sm">{errorMsg}</p>
              <Button variant="outline" className="mt-4" onClick={loadPosts}>Retry</Button>
            </div>
          ) : totalCount === 0 ? (
            <div className="py-16 text-center">
              <CalendarIcon className="w-10 h-10 mx-auto text-[#D3CCBE]" />
              <p className="mt-3 text-[#1E2A33] font-semibold" style={{ fontFamily: 'Manrope' }}>{t('social.no_posts')}</p>
              <p className="text-sm text-[#5C6670] mt-1">Plan your first post across Instagram, LinkedIn, TikTok, and more.</p>
              <Button onClick={openCreate} disabled={!projects.length} className="mt-4 bg-[#A62B1F] hover:bg-[#8C2318] text-white">
                <Plus className="w-4 h-4 mr-2" /> {t('social.new_post')}
              </Button>
            </div>
          ) : viewMode === 'board' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {columns.map((col) => (
                <div key={col.value} className="bg-[#FBF8F2] rounded-lg p-3 min-h-[200px] border border-[#E3DDD1]">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>{col.label}</h3>
                    <span className="text-xs text-[#5C6670] bg-white rounded-full px-2 py-0.5 border border-[#E3DDD1]">{col.posts.length}</span>
                  </div>
                  <div className="space-y-2">
                    {col.posts.length === 0 ? (
                      <p className="text-xs text-[#9AA3AB] py-6 text-center">No posts</p>
                    ) : (
                      col.posts.map((post) => (
                        <BoardPost
                          key={post._id} post={post}
                          projectName={showingAll ? projectName(post.project_id) : undefined}
                          onPreview={setPreviewPost} onEdit={openEdit}
                          onDuplicate={duplicate} onDelete={setDeleteTarget}
                        />
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : viewMode === 'month' ? (
            <MonthView posts={filtered} currentDate={currentDate} onPreview={setPreviewPost} />
          ) : viewMode === 'week' ? (
            <WeekView posts={filtered} currentDate={currentDate} onPreview={setPreviewPost} />
          ) : (
            <DayView
              posts={filtered} currentDate={currentDate} onPreview={setPreviewPost}
              onEdit={openEdit} onDuplicate={duplicate} onDelete={setDeleteTarget}
              showingAll={showingAll} projectName={projectName}
            />
          )}
        </CardContent>
      </Card>

      {/* Post preview */}
      {previewPost && (
        <PostPreviewDialog
          post={previewPost}
          onClose={() => setPreviewPost(null)}
          onEdit={(p) => { setPreviewPost(null); openEdit(p); }}
          onDelete={(p) => { setPreviewPost(null); setDeleteTarget(p); }}
          projectName={showingAll ? projectName(previewPost.project_id) : undefined}
        />
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? t('social.edit_post') : t('social.create_post')}</DialogTitle>
            <DialogDescription>
              {editingPost ? 'Update this post in the schedule.' : 'Posts are saved to the schedule only. Make.com will publish them at the scheduled time.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Project</Label>
                <Select value={form.project_id || NONE} onValueChange={v => setForm(f => ({ ...f, project_id: v === NONE ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No project</SelectItem>
                    {projects.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Platform</Label>
                <Select value={form.platform} onValueChange={v => setForm(f => ({ ...f, platform: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Content type</Label>
                <Select value={form.content_type} onValueChange={v => setForm(f => ({ ...f, content_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTENT_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title (internal)</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Short working title for your team" />
            </div>
            <div>
              <Label>Caption / copy</Label>
              <Textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="The caption that will go out..." rows={5} />
            </div>
            <div>
              <Label>Hashtags (comma separated)</Label>
              <Input value={form.hashtags} onChange={e => setForm(f => ({ ...f, hashtags: e.target.value }))} placeholder="healthcare, wellness, clinic" />
            </div>

            {/* Media upload */}
            <div className="space-y-2">
              <Label>Media</Label>
              <div
                className="border-2 border-dashed border-[#E3DDD1] rounded-lg p-4 text-center cursor-pointer hover:border-[#A62B1F] hover:bg-[#A62B1F]/5 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleFilesSelected(e.dataTransfer.files); }}
              >
                <Upload className="w-5 h-5 mx-auto text-[#5C6670] mb-1" />
                <p className="text-xs text-[#5C6670]">Drag & drop images or videos, or <span className="text-[#A62B1F] font-medium">click to browse</span></p>
                <p className="text-[10px] text-[#9AA3AB] mt-0.5">JPG, PNG, GIF, MP4, MOV · max 20 MB each</p>
                <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={e => handleFilesSelected(e.target.files)} />
              </div>

              {mediaItems.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {mediaItems.map((item) => (
                    <div key={item.id} className="relative group rounded-lg overflow-hidden bg-muted aspect-square border border-[#E3DDD1]">
                      {item.uploading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <Loader2 className="w-5 h-5 text-white animate-spin" />
                        </div>
                      ) : item.isImage ? (
                        <img src={item.preview} alt={item.filename} className="w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-800">
                          <Play className="w-6 h-6 text-white/70" />
                          <span className="text-[10px] text-white/60 px-1 text-center truncate w-full">{item.filename}</span>
                        </div>
                      )}
                      {!item.uploading && (
                        <button
                          type="button"
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeMediaItem(item.id)}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <div className="relative flex-1">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6670]" />
                  <Input value={urlInput} onChange={e => setUrlInput(e.target.value)} onKeyDown={e => e.key==='Enter' && (e.preventDefault(), addUrlMedia())} placeholder="Or paste an image / video URL..." className="pl-8 text-sm" />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addUrlMedia} disabled={!urlInput.trim()}>Add</Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Scheduled date/time</Label>
                <Input type="datetime-local" value={form.scheduled_for} onChange={e => setForm(f => ({ ...f, scheduled_for: e.target.value }))} />
              </div>
              <div>
                <Label>Campaign tag</Label>
                <Input value={form.campaign_tag} onChange={e => setForm(f => ({ ...f, campaign_tag: e.target.value }))} placeholder="Q2-launch" />
              </div>
            </div>
            <div>
              <Label>Internal notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Briefing, approvals, reminders..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>{t('common.cancel')}</Button>
            <Button onClick={submit} disabled={submitting} className="bg-[#A62B1F] hover:bg-[#8C2318] text-white">
              {submitting ? 'Saving...' : editingPost ? 'Save' : 'Schedule Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete post?</AlertDialogTitle>
            <AlertDialogDescription>This post will be removed from the schedule. Use "Cancelled" status instead if you want to keep a record.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
